import { useEffect, useMemo, useRef, useState } from 'react';
import { useEffectOnce } from 'react-use';
import clock from '~/assets/icons/clock.svg';
import { Text } from '~/components/ui/Text';
import { rage, useObservable } from '~/rage';
import { formatTime, numWord } from '~/shared';
import {
	StyledContainer,
	StyledErrorText,
	StyledLogo,
	StyledPincodeContainer,
	StyledPincodeDot,
	StyledPincodeDotsContainer,
	StyledTimerContainer,
} from './AppBankStartScreen.styled';

interface IAppBankStartScreenProps {
	onSuccess?: () => void;
}
const AppBankStartScreen: React.FC<IAppBankStartScreenProps> = ({
	onSuccess,
}) => {
	const banExpiresAt = useObservable(rage.bank.getBanExpiresAt());
	const pinCodeLength = useObservable(rage.bank.getPinCodeLength());
	const [showPincode, setShowPincode] = useState(false);
	const [attemptsLeft, setAttemptsLeft] = useState(0);
	const [showError, setShowError] = useState(false);
	const [pincode, setPincode] = useState('');
	const timerTextRef = useRef<HTMLDivElement>(null);
	const [isTimerVisible, setIsTimerVisible] = useState(() =>
		banExpiresAt ? banExpiresAt - Date.now() > 0 : false,
	);

	useEffectOnce(() => {
		const timeout = setTimeout(() => {
			setShowPincode(true);
		}, 1000);

		return () => clearTimeout(timeout);
	});

	useEffect(() => {
		if (!showPincode || isTimerVisible) return;

		rage.input
			.setAnyInputFocused(true)
			.catch((err) => rage.notifications.showException(err));

		const handleGlobalKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Backspace' || e.key === 'Delete') {
				setPincode((prev) => prev.slice(0, -1));
				return;
			}

			if (!/^\d$/.test(e.key)) return;

			setPincode((prev) => {
				if (prev.length >= pinCodeLength) {
					return prev;
				}

				return (prev + e.key).slice(0, pinCodeLength);
			});
		};

		window.addEventListener('keydown', handleGlobalKeyDown);

		return () => {
			window.removeEventListener('keydown', handleGlobalKeyDown);
			rage.input
				.setAnyInputFocused(false)
				.catch((err) => rage.notifications.showException(err));
		};
	}, [showPincode, onSuccess, isTimerVisible]);

	const handleShowError = (resetFn: () => void) => {
		setShowError(true);
		setTimeout(() => {
			setShowError(false);
			resetFn();
		}, 3000);
	};

	useEffect(() => {
		if (pincode.length !== pinCodeLength) {
			return;
		}

		rage.bank
			.checkPinCode(pincode)
			.then(({ isSucceed, attemptsLeft }) => {
				if (isSucceed) {
					onSuccess?.();
				} else {
					setAttemptsLeft(attemptsLeft ?? 0);
					handleShowError(() => setPincode(''));
				}
			})
			.catch((err) => rage.notifications.showException(err));
	}, [pincode]);

	useEffect(() => {
		if (!banExpiresAt) {
			setIsTimerVisible(false);
			return;
		}

		let intervalId: NodeJS.Timer | undefined = undefined;

		const update = () => {
			const diff = banExpiresAt - Date.now();

			setIsTimerVisible(diff > 0);

			if (diff <= 0) {
				clearInterval(intervalId);
				if (timerTextRef.current) {
					timerTextRef.current.textContent = '00:00';
				}
				return;
			}

			if (!timerTextRef.current) return;

			timerTextRef.current.textContent =
				formatTime(diff, '{mm}:{ss}') || '00:00';
		};

		update();
		intervalId = setInterval(update, 1000);

		return () => {
			clearInterval(intervalId);
		};
	}, [banExpiresAt]);

	const titleText = useMemo(() => {
		if (attemptsLeft > 0) {
			return `Остал${
				attemptsLeft === 1 ? 'а' : 'о'
			}сь ${attemptsLeft} ${numWord(attemptsLeft, [
				'попытка',
				'попытки',
				'попыток',
			])}`;
		}

		if (isTimerVisible) {
			return 'Повторите попытку позже';
		}

		return 'Введите пин-код';
	}, [isTimerVisible, attemptsLeft]);

	return (
		<StyledContainer>
			<StyledLogo $top={showPincode || isTimerVisible} />
			{(showError || isTimerVisible) && (
				<StyledErrorText>Неправильный пин-код</StyledErrorText>
			)}
			<StyledPincodeContainer $show={showPincode || isTimerVisible}>
				<Text>{titleText}</Text>
				<StyledPincodeDotsContainer $length={pinCodeLength}>
					{Array.from({ length: pinCodeLength }).map((_, index) => (
						<StyledPincodeDot
							key={index}
							$active={index < pincode.length}
							$error={showError || isTimerVisible}
						/>
					))}
				</StyledPincodeDotsContainer>
			</StyledPincodeContainer>
			<StyledTimerContainer $show={isTimerVisible}>
				<img src={clock} />
				<Text ref={timerTextRef}>
					{banExpiresAt
						? formatTime(Math.max(banExpiresAt - Date.now(), 0), '{mm}:{ss}')
						: '00:00'}
				</Text>
			</StyledTimerContainer>
		</StyledContainer>
	);
};

export { AppBankStartScreen };
